import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import tw from 'twin.macro';

import { Button } from '@components/Button';
import { Spacer } from '@components/Spacer';
import { CloseIcon } from '@icons/CloseIcon';
import { Loader } from '@components/Loader';
import { Modal, ModalContent } from '@components/Modal';

import { removeUser } from '@account_module/services';
import { ORGANIZATION_QUERY_KEY } from '@account_module/constants/queryKeys';
import { useOrganizationUsers } from '@account_module/hooks/useOrganizationUsers';
import { useAccountSettingContext } from '@account_module/context/AccountSettingsContext';
import { useNotification } from '@insights_module/hooks/useNotification';
import { IOrganizationUser } from '@account_module/types/Organizations';

const ModalButton = tw(
  Button.Primary
)`text-[11px] w-auto h-auto py-[6px] px-[10px] whitespace-nowrap bg-transparent `;
const CloseButton = tw(ModalButton)`text-text-and-icon border border-solid border-gray-1`;
const Flex = tw.div`flex items-center justify-between gap-2`;
const Title = tw.p`text-[18px] font-bold`;
const Description = tw.p`text-left text-[14px]`;

const UserContainer = tw.div`text-left mt-2 flex items-center justify-start`;
const UserAvatar = tw.div`h-6 w-6 bg-accent-1 rounded-full mr-2 text-text-and-icon-invert text-[13px] whitespace-nowrap flex items-center justify-center`;
const UserEmail = tw.span`font-semibold text-[13px] text-text-and-icon`;

async function removeServiceFn(orgId: string | undefined, userId: string | null) {
  return await removeUser(orgId, userId);
}

async function removeBulkServiceFn(orgId: string | undefined, ids: string[]) {
  const requests = ids.map(async (user) => {
    return await removeUser(orgId, user);
  });

  return Promise.all(requests);
}

export const RemoveUserModal = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { NTFC_TYPE, notify } = useNotification();
  const {
    selectedUsers,
    currentOrganization,
    selectedRow,
    setSelectedUsers,
    setSelectedRow,
    setIsRemoveUserModalActive,
  } = useAccountSettingContext();
  const { data: users } = useOrganizationUsers(currentOrganization);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isBulk = selectedUsers.length > 1;

  const userToRemove = users?.find((user) => user.id === selectedRow);

  const handleCancel = () => {
    setIsRemoveUserModalActive(false);
    setSelectedRow(null);
  };

  const handleSuccess = (msg: string) => {
    notify({
      type: NTFC_TYPE.INFO,
      message: msg,
    });
    setIsRemoveUserModalActive(false);
  };

  const handleError = (msg: string) => {
    console.error(msg);
  };

  const handleFinally = () => {
    setIsLoading(false);
    setSelectedUsers([]);
    queryClient.invalidateQueries([ORGANIZATION_QUERY_KEY.users]);
  };

  const handleRemoveUser = async () => {
    setIsLoading(true);

    if (selectedUsers.length > 0) {
      removeBulkServiceFn(currentOrganization, selectedUsers)
        .then(() => handleSuccess(`${selectedUsers?.length} team members has been removed`))
        .catch((error) => handleError(error.message))
        .finally(handleFinally);
    } else {
      removeServiceFn(currentOrganization, selectedRow)
        .then(() => handleSuccess('Team member has been removed.'))
        .catch((error) => handleError(error.message))
        .finally(handleFinally);
    }
  };

  const getUserInitials = (user: IOrganizationUser | undefined): string => {
    if (!user) {
      return '-';
    }

    return `${user?.first_name[0]}${user.last_name[0]}`;
  };

  return (
    <Modal onClose={() => setIsRemoveUserModalActive(false)}>
      <ModalContent tw='bg-page-background border border-solid border-gray-1 shadow-modal p-4'>
        <Flex>
          <Title>
            {isBulk
              ? t('account_module.settings_page.remove_users_modal.title_plural')
              : t('account_module.settings_page.remove_users_modal.title_singular')}
          </Title>
          <button onClick={handleCancel}>
            <CloseIcon tw='fill-primary' />
          </button>
        </Flex>
        <Spacer direction='vertical' size='xl' />
        <Description>
          {isBulk ? (
            <Trans
              i18nKey='account_module.settings_page.remove_users_modal.description_plural'
              values={{ count: selectedUsers.length }}>
              Are you sure you want to remove this <span /> members from the team?
            </Trans>
          ) : (
            t('account_module.settings_page.remove_users_modal.description_singular')
          )}
        </Description>
        {!selectedUsers.length && !!selectedRow && (
          <UserContainer key={userToRemove?.id}>
            <UserAvatar>{getUserInitials(userToRemove)}</UserAvatar>
            <UserEmail>{userToRemove?.email}</UserEmail>
          </UserContainer>
        )}
        <Spacer direction='vertical' size='xxxl' />
        <Flex tw='w-full justify-end'>
          <CloseButton onClick={handleCancel}>
            {t('account_module.settings_page.remove_users_modal.cancel')}
          </CloseButton>

          <ModalButton
            tw='text-error-text-icon bg-error-bg border-error-bg h-[30px] w-[107px]'
            onClick={handleRemoveUser}>
            {isLoading ? (
              <Loader theme='dark' size='xs' />
            ) : (
              t('account_module.settings_page.remove_users_modal.cta')
            )}
          </ModalButton>
        </Flex>
      </ModalContent>
    </Modal>
  );
};
